import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Camera,
  Sparkles,
  User,
  Briefcase,
  Phone,
  CheckCircle2,
  Trash2,
  Wallet,
  IndianRupee,
  Shield,
  ExternalLink,
  KeyRound,
  UserMinus,
} from 'lucide-react'
import DefaultImagePNG from '../../../../assets/images/DefaultProfilePic.png'
import { useAppDispatch, useAppSelector } from '../../../../Redux/Hooks'
import {
  deleteMemberAPI,
  updateMemberAPI,
  getAllMembers,
} from '../../../../Redux/ApiCalls/Dashboard/MembersAPI'
import { getTeamMemberPaymentsAPI } from '../../../../Redux/ApiCalls/Dashboard/TeamMemberPaymentApi'
import { clearFlagsMembers, setCurrentMember } from '../../../../Redux/Slice/Dashboard/MemberSlice'
import { getAllRolesAPI, assignRoleAPI, unassignRoleAPI } from '../../../../Redux/ApiCalls/Dashboard/RoleApi'
import { usePermissions } from '../../../../hooks/usePermissions'
import PermissionGate from '../../../atoms/Utlis/PermissionGate'
import { showSuccessToast, showErrorToast } from '../../../atoms/Utlis/Toast'
import LoadingDots from '../../../atoms/Utlis/LoadinDots'
import imageCompression from 'browser-image-compression'
import DeletePopup from '../../../atoms/Dashboard/Folder/DeletePopup'
import CreateManualPaymentModal from '../../../atoms/Dashboard/TeamMemberPayment/CreateManualPaymentModal'
import UpdatePaymentModal from '../../../atoms/Dashboard/TeamMemberPayment/UpdatePaymentModal'
import TeamMemberPaymentList from '../../../atoms/Dashboard/TeamMemberPayment/TeamMemberPaymentList'
import { IMAGE_CDN } from '../../../../config/config'
import type { TeamMemberPayment } from '../../../../Data/teamMemberPayment.dto'
import {
  PageContainer,
  Hero,
  HeroGlow,
  HeroRow,
  HeroLeft,
  BackButton,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
  ScrollBody,
  FormGrid,
  FormCard,
  CardHeader,
  CardIcon,
  CardTitle,
  CardSubtitle,
  PhotoUploadArea,
  PhotoRing,
  MemberImage,
  PhotoBadge,
  PhotoHint,
  PhotoSubhint,
  FieldGrid,
  Field,
  FieldFull,
  Label,
  RequiredStar,
  FieldHint,
  Input,
  Select,
  PhoneRow,
  CountryCodeBox,
  CountryCodeInput,
  PhoneInput,
  ErrorSpan,
  SubmitButton,
  DeleteBtn,
  JOB_TYPES,
  EMPLOYMENT_TYPES,
  gradient,
} from './memberFormStyles'

const EditMemberPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [name, setName] = useState<string>('')
  const [jobType, setJobType] = useState<string>('')
  const [employmentType, setEmploymentType] = useState<string>('Full Time')
  const navigate = useNavigate()
  const [selectedImage, setSelectedImage] = useState<File>()
  const [imagePreview, setImagePreview] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [countryCode, setCountryCode] = useState<string>('+91')
  const [contact, setContact] = useState<string>('')
  const [activeButton, setActiveButton] = useState<boolean>(false)
  const [showError, setShowError] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const [deleteMemberPopup, setDeleteMemberPopup] = useState<boolean>(false)
  const [showCreatePaymentModal, setShowCreatePaymentModal] = useState(false)
  const [updatePaymentId, setUpdatePaymentId] = useState<number | string | null>(null)
  const { loading, success, currentMember, members, isMemberFetched } = useAppSelector((state) => state.member)
  const { branches } = useAppSelector((state) => state.auth)
  const [branchId, setBranchId] = useState<number | string>('')
  const { payments, team_member_payments: paymentSummary, loading: paymentsLoading } =
    useAppSelector((state) => state.teamMemberPayment)
  const { roles, loading: roleLoading, isRolesFetched } = useAppSelector((state) => state.role)
  const [assignedRoleId, setAssignedRoleId] = useState<number | string>('')
  const [initialRoleId, setInitialRoleId] = useState<number | string>('')
  const [isAssigningRole, setIsAssigningRole] = useState(false)

  const { hasPermission, hasAnyPermission, permissionsLoading } = usePermissions()
  const canViewRoles = hasAnyPermission(['role_management.read', 'role_management.update'])
  const canUpdateRoles = hasPermission('role_management.update')
  const canViewPayments = hasAnyPermission([
    'payments_management.read',
    'payments_management.create',
    'payments_management.update',
  ])

  // Hydrate currentMember if page is directly loaded or refreshed
  useEffect(() => {
    if (!currentMember && id) {
      if (!isMemberFetched) {
        dispatch(getAllMembers())
      } else if (members && members.length > 0) {
        const found = members.find((m) => String(m.id) === String(id))
        if (found) {
          dispatch(setCurrentMember(found))
        }
      }
    }
  }, [currentMember, id, isMemberFetched, members, dispatch])

  // Only fetch studio roles if user has role viewing permission
  useEffect(() => {
    if (canViewRoles && !permissionsLoading && !isRolesFetched && roles.length === 0) {
      dispatch(getAllRolesAPI())
    }
  }, [canViewRoles, permissionsLoading, isRolesFetched, roles.length, dispatch])

  useEffect(() => {
    if (currentMember && canViewRoles) {
      let foundRoleId: number | string = ''
      if (roles.length > 0) {
        const foundRole = roles.find((r) =>
          r.assigned_members?.some((m) => String(m.id) === String(currentMember.id))
        )
        if (foundRole) {
          foundRoleId = foundRole.id
        }
      }
      if (!foundRoleId && (currentMember as any).role?.id) {
        foundRoleId = (currentMember as any).role.id
      }
      setAssignedRoleId(foundRoleId)
      setInitialRoleId(foundRoleId)
    }
  }, [currentMember, roles, canViewRoles])

  const handleAssignRole = async () => {
    if (!currentMember || !canViewRoles) return

    // Case 1: Unassign (cleared role)
    if (!assignedRoleId) {
      if (!initialRoleId) {
        showErrorToast('Please select a studio role to assign.')
        return
      }
      setIsAssigningRole(true)
      try {
        await dispatch(
          unassignRoleAPI({
            roleId: initialRoleId,
            teamMemberId: currentMember.id,
          })
        ).unwrap()
        showSuccessToast('Role removed from team member.')
        setInitialRoleId('')
        if (canViewRoles) dispatch(getAllRolesAPI())
      } catch (err: any) {
        showErrorToast(err?.message || 'Failed to remove role')
      } finally {
        setIsAssigningRole(false)
      }
      return
    }

    // Case 2: Assign or Update role
    setIsAssigningRole(true)
    try {
      const res = await dispatch(
        assignRoleAPI({
          roleId: assignedRoleId,
          teamMemberId: currentMember.id,
        })
      ).unwrap()
      const msg = res?.result?.data?.login_hint || 'Role assigned successfully!'
      showSuccessToast(msg)
      setInitialRoleId(assignedRoleId)
      if (canViewRoles) dispatch(getAllRolesAPI())
    } catch (err: any) {
      showErrorToast(err?.message || 'Failed to assign role')
    } finally {
      setIsAssigningRole(false)
    }
  }

  useEffect(() => {
    if (success) navigate('/dashboard/members/all')
    return () => { dispatch(clearFlagsMembers()) }
  }, [success, navigate, dispatch])

  useEffect(() => {
    if (currentMember) {
      setName(currentMember.name)
      setJobType(currentMember.job_type)
      setEmploymentType(currentMember.employment_type || 'Full Time')
      setContact(currentMember.phone_number)
      setCountryCode(currentMember.country_code)
      setBranchId(
        currentMember.branch_id != null
          ? String(currentMember.branch_id)
          : ''
      )
      setImagePreview(`${IMAGE_CDN}/${currentMember.profile_image}`)
    }
  }, [currentMember])

  // Only fetch payments if user has payment viewing permission
  useEffect(() => {
    if (currentMember && canViewPayments && !permissionsLoading) {
      dispatch(
        getTeamMemberPaymentsAPI({
          team_member_id: currentMember.id,
          page_size: 50,
          page: 1,
        })
      )
    }
  }, [currentMember?.id, canViewPayments, permissionsLoading, dispatch])

  const handleDivClick = () => fileInputRef.current?.click()

  const blobToFile = (blob: Blob, fileName: string): File =>
    new File([blob], fileName, { type: blob.type })

  const compressImage = async (file: File): Promise<Blob> =>
    imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: false })

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.[0]) return
    let file = event.target.files[0]
    if (file.size / 1024 / 1024 > 2) {
      file = blobToFile(await compressImage(file), file.name)
    }
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onloadend = () => {
      setSelectedImage(file)
      setImagePreview(reader.result as string)
    }
  }

  const validatePhoneNumber = (phoneNumber: string): boolean =>
    /^[0-9]{10}$/.test(phoneNumber)

  const isValidData = () => {
    const baseValid =
      name.trim().length > 0 &&
      jobType.trim().length > 0 &&
      (selectedImage || currentMember?.profile_image) &&
      validatePhoneNumber(contact) &&
      countryCode.trim().length > 0 &&
      countryCode.trim().length <= 4

    if (baseValid && currentMember) {
      const nameChanged = (currentMember.name || '').trim() !== name.trim()
      const jobTypeChanged = currentMember.job_type !== jobType
      const employmentTypeChanged = (currentMember.employment_type || 'Full Time') !== employmentType
      const phoneChanged =
        (currentMember.phone_number || '').trim() !== contact.trim() ||
        (currentMember.country_code || '').trim() !== countryCode.trim()
      const initialBranchStr =
        currentMember.branch_id != null
          ? String(currentMember.branch_id)
          : ''
      const currentBranchStr = branchId != null ? String(branchId) : ''
      const branchChanged = initialBranchStr !== currentBranchStr
      const imageChanged = !!selectedImage
      const roleChanged =
        canViewRoles && String(assignedRoleId || '') !== String(initialRoleId || '')

      const changed =
        nameChanged ||
        jobTypeChanged ||
        employmentTypeChanged ||
        phoneChanged ||
        branchChanged ||
        imageChanged ||
        roleChanged

      setActiveButton(changed)
    } else {
      setActiveButton(false)
    }
    setShowError(false)
  }

  useEffect(() => {
    isValidData()
  }, [
    selectedImage,
    name,
    jobType,
    employmentType,
    contact,
    countryCode,
    branchId,
    assignedRoleId,
    initialRoleId,
    currentMember,
    canViewRoles,
  ])

  const handleSubmit = async () => {
    if (!activeButton || !currentMember) {
      setShowError(true)
      return
    }

    const nameChanged = (currentMember.name || '').trim() !== name.trim()
    const jobTypeChanged = currentMember.job_type !== jobType
    const employmentTypeChanged = (currentMember.employment_type || 'Full Time') !== employmentType
    const phoneChanged =
      (currentMember.phone_number || '').trim() !== contact.trim() ||
      (currentMember.country_code || '').trim() !== countryCode.trim()
    const initialBranchStr =
      currentMember.branch_id != null
        ? String(currentMember.branch_id)
        : ''
    const currentBranchStr = branchId != null ? String(branchId) : ''
    const branchChanged = initialBranchStr !== currentBranchStr
    const imageChanged = !!selectedImage
    const roleChanged =
      canViewRoles && String(assignedRoleId || '') !== String(initialRoleId || '')

    const memberDetailsChanged =
      nameChanged ||
      jobTypeChanged ||
      employmentTypeChanged ||
      phoneChanged ||
      branchChanged ||
      imageChanged

    try {
      // 1. If role was changed, update/unassign role
      if (roleChanged && canViewRoles) {
        setIsAssigningRole(true)
        if (!assignedRoleId) {
          if (initialRoleId) {
            await dispatch(
              unassignRoleAPI({
                roleId: initialRoleId,
                teamMemberId: currentMember.id,
              })
            ).unwrap()
            setInitialRoleId('')
            if (canViewRoles) dispatch(getAllRolesAPI())
          }
        } else {
          await dispatch(
            assignRoleAPI({
              roleId: assignedRoleId,
              teamMemberId: currentMember.id,
            })
          ).unwrap()
          setInitialRoleId(assignedRoleId)
          if (canViewRoles) dispatch(getAllRolesAPI())
        }
        setIsAssigningRole(false)
      }

      // 2. If member profile details were changed, send ONLY the changed fields in PUT API
      if (memberDetailsChanged) {
        const formData = new FormData()
        formData.append('id', `${currentMember.id}`)
        if (nameChanged) formData.append('name', name.trim())
        if (jobTypeChanged) formData.append('job_type', jobType)
        if (employmentTypeChanged) formData.append('employment_type', employmentType)
        if (phoneChanged) {
          formData.append('country_code', countryCode.trim())
          formData.append('phone_number', contact.trim())
        }
        if (branchChanged) {
          formData.append('branch_id', branchId ? String(branchId) : '')
        }
        if (imageChanged && selectedImage) {
          formData.append('profile_image', selectedImage)
        }
        await dispatch(updateMemberAPI(formData)).unwrap()
      } else if (roleChanged) {
        // If only role was changed (no member details changed), show success and navigate
        showSuccessToast('Role updated successfully!')
        dispatch(getAllMembers())
        navigate('/dashboard/members/all')
      }
    } catch (err: any) {
      setIsAssigningRole(false)
      console.error('Failed to save changes:', err)
    }
  }

  const handleDeleteMember = (id: number) => {
    dispatch(deleteMemberAPI({ member_id: id }))
    setDeleteMemberPopup(false)
  }

  const refetchMemberPayments = () => {
    if (currentMember && canViewPayments) {
      dispatch(
        getTeamMemberPaymentsAPI({
          team_member_id: currentMember.id,
          page_size: 50,
          page: 1,
        })
      )
    }
  }

  const formatSummaryAmount = (value: number) =>
    `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`

  const memberPayments: TeamMemberPayment[] = currentMember && canViewPayments
    ? (payments ?? []).filter((p) => String(p.team_member_id) === String(currentMember.id))
    : []

  if (!currentMember) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <LoadingDots />
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      {deleteMemberPopup && (
        <DeletePopup
          Delete={() => handleDeleteMember(parseInt(currentMember.id))}
          cancel={() => setDeleteMemberPopup(false)}
          text="Are you sure you want to delete this member?"
          buttonText="Delete"
        />
      )}

      <Hero>
        <HeroGlow />
        <HeroRow>
          <HeroLeft>
            <BackButton type="button" onClick={() => navigate(-1)} aria-label="Go back">
              <ArrowLeft size={18} />
            </BackButton>
            <HeroText>
              <div className="flex flex-col">
                <HeroBadge>
                  <Sparkles size={12} />
                  Team profile
                </HeroBadge>
                <PageTitle className="linear-text">Update Member</PageTitle>
                <PageSubtitle>
                  Edit {currentMember.name}&apos;s details{canViewRoles ? ', role' : ''}{canViewPayments ? ', and payment history' : ''}.
                </PageSubtitle>
              </div>
            </HeroText>
          </HeroLeft>
          <HeroActions>
            <PermissionGate permission="members.delete">
              <DeleteBtn type="button" onClick={() => setDeleteMemberPopup(true)}>
                <Trash2 size={14} />
                Delete
              </DeleteBtn>
            </PermissionGate>
            {loading ? (
              <LoadingDots />
            ) : (
              <PermissionGate permission="members.update">
                <HeroSaveBtn type="button" $ready={activeButton} onClick={handleSubmit}>
                  {activeButton && <CheckCircle2 size={16} />}
                  Save changes
                </HeroSaveBtn>
              </PermissionGate>
            )}
          </HeroActions>
        </HeroRow>
      </Hero>

      <ScrollBody>
        <FormGrid>
          <FormCard>
            <CardHeader>
              <CardIcon>
                <User size={20} />
              </CardIcon>
              <div>
                <CardTitle>Profile photo</CardTitle>
                <CardSubtitle>Update how this member appears in your studio</CardSubtitle>
              </div>
            </CardHeader>
            <PhotoUploadArea onClick={handleDivClick}>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
                ref={fileInputRef}
              />
              <PhotoRing>
                <MemberImage
                  src={imagePreview || DefaultImagePNG}
                  alt={name}
                />
                <PhotoBadge>
                  <Camera size={16} />
                </PhotoBadge>
              </PhotoRing>
              <PhotoHint>Tap to update photo</PhotoHint>
              <PhotoSubhint>JPG or PNG · Max 2 MB</PhotoSubhint>
            </PhotoUploadArea>
            <Field>
              <Label>
                Full name<RequiredStar>*</RequiredStar>
              </Label>
              <Input
                value={name}
                onChange={(e) => {
                  if (e.target.value.length <= 25) setName(e.target.value)
                }}
                placeholder="Enter member name"
              />
              {!name.length && showError && (
                <ErrorSpan>Please enter member name.</ErrorSpan>
              )}
            </Field>
          </FormCard>

          <FormCard>
            <CardHeader>
              <CardIcon $tone="#35b7c8">
                <Briefcase size={20} />
              </CardIcon>
              <div>
                <CardTitle>Role & contact</CardTitle>
                <CardSubtitle>Role, phone, and employment details</CardSubtitle>
              </div>
            </CardHeader>
            <FieldGrid>
              <FieldFull>
                <Label>
                  Job type<RequiredStar>*</RequiredStar>
                </Label>
                <Select value={jobType} onChange={(e) => setJobType(String(e.target.value))}>
                  {JOB_TYPES.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </Select>
                {!jobType.length && showError && (
                  <ErrorSpan>Please select a job type.</ErrorSpan>
                )}
              </FieldFull>
              <FieldFull>
                <Label>Branch</Label>
                <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                  <option value="">All Branches / Studio HQ (No branch)</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.branch_name} ({b.branch_city})
                    </option>
                  ))}
                </Select>
              </FieldFull>
              <FieldFull>
                <Label>
                  Phone number<RequiredStar>*</RequiredStar>
                </Label>
                <PhoneRow>
                  <CountryCodeBox>
                    <Phone size={16} color="#c94daa" />
                    <CountryCodeInput
                      value={countryCode}
                      onChange={(e) => {
                        if (e.target.value.length <= 4) setCountryCode(e.target.value)
                      }}
                      placeholder="+91"
                    />
                  </CountryCodeBox>
                  <PhoneInput
                    value={contact}
                    onChange={(e) => {
                      const digitsOnly = e.target.value.replace(/\D/g, '')
                      if (digitsOnly.length <= 10) setContact(digitsOnly)
                    }}
                    placeholder="10-digit mobile number"
                    type="tel"
                    inputMode="numeric"
                  />
                </PhoneRow>
                {!(contact.length === 10 && validatePhoneNumber(contact)) && showError && (
                  <ErrorSpan>Please enter a valid 10-digit phone number.</ErrorSpan>
                )}
              </FieldFull>
              <FieldFull>
                <Label>
                  Employment type<RequiredStar>*</RequiredStar>
                </Label>
                <Select
                  value={employmentType}
                  onChange={(e) => setEmploymentType(e.target.value)}
                >
                  {EMPLOYMENT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Select>
              </FieldFull>
            </FieldGrid>
          </FormCard>
        </FormGrid>

        {canViewRoles && (
          <RoleAccessCard>
            <RoleAccessHeader>
              <RoleAccessHeaderLeft>
                <CardIcon $tone="#c94daa">
                  <Shield size={20} />
                </CardIcon>
                <div>
                  <CardTitle>Studio Role & Access</CardTitle>
                  <CardSubtitle>
                    Assign a studio role to grant module permissions and enable phone OTP login (Role 4).
                  </CardSubtitle>
                </div>
              </RoleAccessHeaderLeft>
              <PermissionGate permission="role_management.read">
                <ManageRolesLink
                  type="button"
                  onClick={() => navigate(`/dashboard/roles?assignMember=${currentMember?.id}`)}
                >
                  <span>Manage in Roles & Access</span>
                  <ExternalLink size={14} />
                </ManageRolesLink>
              </PermissionGate>
            </RoleAccessHeader>

            <RoleAccessBody>
              <RoleSelectRow>
                <RoleSelectField>
                  <Label>Assigned Studio Role</Label>
                  <Select
                    value={assignedRoleId}
                    onChange={(e) => setAssignedRoleId(e.target.value)}
                    disabled={!canUpdateRoles}
                  >
                    <option value="">-- No custom role assigned (Restricted) --</option>
                    {roles.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} ({r.permissions?.length || 0} permissions)
                      </option>
                    ))}
                  </Select>
                </RoleSelectField>

                <PermissionGate permission="role_management.update">
                  <AssignRoleButton
                    type="button"
                    onClick={handleAssignRole}
                    disabled={
                      isAssigningRole ||
                      String(assignedRoleId) === String(initialRoleId)
                    }
                    style={
                      !assignedRoleId && initialRoleId
                        ? { background: '#ef4444' }
                        : undefined
                    }
                  >
                    {isAssigningRole ? (
                      <span>Saving...</span>
                    ) : !assignedRoleId && initialRoleId ? (
                      <>
                        <UserMinus size={15} />
                        <span>Remove Role</span>
                      </>
                    ) : String(assignedRoleId) === String(initialRoleId) && initialRoleId ? (
                      <>
                        <CheckCircle2 size={15} />
                        <span>Assigned</span>
                      </>
                    ) : (
                      <>
                        <KeyRound size={15} />
                        <span>{initialRoleId ? "Update Role" : "Assign Role"}</span>
                      </>
                    )}
                  </AssignRoleButton>
                </PermissionGate>
              </RoleSelectRow>

              {assignedRoleId ? (
                <RoleDetailsHint>
                  <Sparkles size={14} color="#c94daa" />
                  <span>
                    {roles.find((r) => String(r.id) === String(assignedRoleId))?.description ||
                      "This member will have access to the permissions specified in this role."}
                  </span>
                </RoleDetailsHint>
              ) : (
                <RoleDetailsHint style={{ background: '#fef2f2', borderColor: '#fecaca' }}>
                  <Shield size={14} color="#ef4444" />
                  <span style={{ color: '#991b1b' }}>
                    Without an assigned studio role, this sub-user will have no access to protected studio modules upon login.
                  </span>
                </RoleDetailsHint>
              )}
            </RoleAccessBody>
          </RoleAccessCard>
        )}

        {canViewPayments && (
          <PaymentsCard>
            <PaymentsHeader>
              <PaymentsHeaderLeft>
                <CardIcon $tone="#31a66a">
                  <Wallet size={20} />
                </CardIcon>
                <div>
                  <CardTitle>Payments</CardTitle>
                  <CardSubtitle>Track payouts and payment history for this member</CardSubtitle>
                </div>
              </PaymentsHeaderLeft>
              <PermissionGate permission="payments_management.create">
                <PayBtn type="button" onClick={() => setShowCreatePaymentModal(true)}>
                  <IndianRupee size={16} />
                  Pay this member
                </PayBtn>
              </PermissionGate>
            </PaymentsHeader>

            {paymentSummary && (
              <SummaryGrid>
                <SummaryCard>
                  <SummaryLabel>Total payments</SummaryLabel>
                  <SummaryValue>{formatSummaryAmount(paymentSummary.total_payments)}</SummaryValue>
                </SummaryCard>
                <SummaryCard $tone="#31a66a">
                  <SummaryLabel>Completed</SummaryLabel>
                  <SummaryValue>{formatSummaryAmount(paymentSummary.payments_completed.total)}</SummaryValue>
                  <SummaryMeta>
                    {paymentSummary.payments_completed.count} payment
                    {paymentSummary.payments_completed.count !== 1 ? 's' : ''}
                  </SummaryMeta>
                </SummaryCard>
                <SummaryCard $tone="#f09a3e">
                  <SummaryLabel>Pending</SummaryLabel>
                  <SummaryValue>{formatSummaryAmount(paymentSummary.payments_pending.total)}</SummaryValue>
                  <SummaryMeta>
                    {paymentSummary.payments_pending.count} payment
                    {paymentSummary.payments_pending.count !== 1 ? 's' : ''}
                  </SummaryMeta>
                </SummaryCard>
              </SummaryGrid>
            )}

            {paymentsLoading && memberPayments.length === 0 ? (
              <LoadingDots />
            ) : (
              <TeamMemberPaymentList
                compact
                embedded
                payments={memberPayments}
                onUpdate={(id) => setUpdatePaymentId(id)}
              />
            )}
          </PaymentsCard>
        )}
      </ScrollBody>

      {canViewPayments && showCreatePaymentModal && (
        <CreateManualPaymentModal
          isOpen={showCreatePaymentModal}
          onClose={() => setShowCreatePaymentModal(false)}
          members={[currentMember]}
          preselectedMemberId={currentMember.id}
          onSuccess={refetchMemberPayments}
        />
      )}
      {canViewPayments && (
        <UpdatePaymentModal
          isOpen={updatePaymentId != null}
          onClose={() => setUpdatePaymentId(null)}
          paymentId={updatePaymentId}
          onSuccess={refetchMemberPayments}
        />
      )}
    </PageContainer>
  )
}

export default EditMemberPage

const HeroActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex-shrink: 0;
`

const HeroSaveBtn = styled(SubmitButton)`
  min-width: unset;
  height: 40px;
  padding: 0 18px;
  font-size: 14px;
`

const PaymentsCard = styled.div`
  background: #fff;
  border-radius: 20px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`

const PaymentsHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`

const PaymentsHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const PayBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.22);
  transition: transform 0.15s ease;
  &:hover { transform: translateY(-1px); }
`

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`

const SummaryCard = styled.div<{ $tone?: string }>`
  padding: 16px;
  border-radius: 14px;
  background: ${(p) => (p.$tone ? `${p.$tone}08` : 'rgba(23, 23, 23, 0.02)')};
  border: 1px solid ${(p) => (p.$tone ? `${p.$tone}20` : 'rgba(23, 23, 23, 0.06)')};
`

const SummaryLabel = styled.p`
  margin: 0 0 4px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const SummaryValue = styled.p`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
`

const SummaryMeta = styled.p`
  margin: 4px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  color: rgba(23, 23, 23, 0.45);
`

const RoleAccessCard = styled.div`
  background: #fff;
  border-radius: 20px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const RoleAccessHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
`

const RoleAccessHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const ManageRolesLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  padding: 8px 14px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #c94daa;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #fdf2f8;
    border-color: #c94daa;
  }
`

const RoleAccessBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const RoleSelectRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
`

const RoleSelectField = styled.div`
  flex: 1;
  min-width: 240px;
`

const AssignRoleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 20px;
  border-radius: 12px;
  border: none;
  background: ${gradient};
  color: #ffffff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(206, 86, 176, 0.25);
  transition: all 0.15s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(206, 86, 176, 0.35);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const RoleDetailsHint = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #faf5ff;
  border: 1px solid rgba(201, 77, 170, 0.15);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: #525252;
`

